import { produce } from 'immer'
import { create } from 'zustand'
import { ProjetosProvider } from 'src/providers/ProjetosProvider'
import { formatarComponentesCalcular } from 'src/components/projetos/utils/formatarComponentesCalcular'
import { ComponenteCalcular, EstruturaListagem, ListagemItem, ModuloPorRegiaoListagemResponse, ProjetoCalcular, ProjetoServico, VinculoModuloResponse } from 'src/components/projetos/types'
import {
  createDefaultProjetoPersonalizadoOffgrid,
  DEFAULT_PROJETO_PERSONALIZADO_OFFGRID_LOADINGS,
  DEFAULT_PROJETO_PERSONALIZADO_OFFGRID_MODALS,
  DEFAULT_PROJETO_PERSONALIZADO_OFFGRID_VALIDATIONS,
} from './personalizadoOffgrid.constants'
import {
  ProjetoPersonalizadoOffgridData,
  ProjetoPersonalizadoOffgridLoadings,
  ProjetoPersonalizadoOffgridModals,
  ProjetoPersonalizadoOffgridValidations,
} from './personalizadoOffgrid.types'
import { CONTROLADOR_MAXIMA_POTENCIA_FOTOVOLTAICA_ATTR, INVERSOR_CARREGADOR_TENSAO_MAXIMA_ENTRADA, INVERSOR_ONDA_POTENCIA_MAXIMA_ATTR, INVERTER_MAX_POWER_ATTR, MODULO_MODULO_TENSAO_VOC_ATTR, MODULO_POTENCIA_MAXIMA_ATTR, MODULO_POTENCIA_MINIMA_ATTR, MODULO_POWER_ATTR } from 'src/components/projetos/constants'

type State = {
  data: ProjetoPersonalizadoOffgridData
  validations: ProjetoPersonalizadoOffgridValidations
  loadings: ProjetoPersonalizadoOffgridLoadings
  modals: ProjetoPersonalizadoOffgridModals
}

type Actions = {
  changeValue: (values: Partial<ProjetoPersonalizadoOffgridData>) => void
  changeValidation: (values: Partial<ProjetoPersonalizadoOffgridValidations>) => void
  changeLoading: (values: Partial<ProjetoPersonalizadoOffgridLoadings>) => void
  changeModal: (values: Partial<ProjetoPersonalizadoOffgridModals>) => void
  reset: () => void
  calcularProjeto: (valorServico?: number) => Promise<void>
  prepararSalvar: () => ProjetoPersonalizadoOffgridData
  salvarProjeto: <T>(save: (payload: ProjetoPersonalizadoOffgridData) => Promise<T>) => Promise<T>
  addModulos: (modulos: ModuloPorRegiaoListagemResponse[], moduloSelecionado?: { id: number, quantidade: number }) => void
  addControladores: (controladores: VinculoModuloResponse[], controladoresSelecionados?: { id: number, quantidade: number }[]) => void
  addCarregadores: (carregadores: VinculoModuloResponse[], carregadoresSelecionados?: { id: number, quantidade: number }[]) => void
  addEstruturas: (estruturas: EstruturaListagem[], componentesProjeto?: { id: number, quantidade: number, grupo_id: number }[]) => void
  addAcessorios: (acessorios: ListagemItem[], componentesProjeto?: { id: number, quantidade: number }[]) => void
}

type Store = { state: State; actions: Actions }

const projetosProvider = new ProjetosProvider()

const initialState = (): State => ({
  data: createDefaultProjetoPersonalizadoOffgrid(),
  validations: { ...DEFAULT_PROJETO_PERSONALIZADO_OFFGRID_VALIDATIONS },
  loadings: { ...DEFAULT_PROJETO_PERSONALIZADO_OFFGRID_LOADINGS },
  modals: { ...DEFAULT_PROJETO_PERSONALIZADO_OFFGRID_MODALS },
})

export const useProjetoPersonalizadoOffgridStore = create<Store>()((set, get) => {
  const update = (recipe: (store: Store) => void) => set(produce(recipe))

  return {
    state: initialState(),
    actions: {
      changeValue: values => update(({ state }) => { Object.assign(state.data, values) }),
      changeValidation: values => update(({ state }) => { Object.assign(state.validations, values)} ),
      changeLoading: values => update(({ state }) => { Object.assign(state.loadings, values) }),
      changeModal: values => update(({ state }) => { Object.assign(state.modals, values) }),
      reset: () => set({ state: initialState() }),
      addModulos: (modulos: ModuloPorRegiaoListagemResponse[], moduloSelecionado?: { id: number, quantidade: number }) => {
        update(({ state }) => {
          const newModulos = modulos.map(modulo => {
            const potencia = modulo.atributos.find(item => item.atributo === MODULO_POWER_ATTR)
            const potenciaMaxima = modulo.atributos.find(item => item.atributo === MODULO_POTENCIA_MAXIMA_ATTR)
            const potenciaMinima = modulo.atributos.find(item => item.atributo === MODULO_POTENCIA_MINIMA_ATTR)
            const tensaoVocModulo = modulo.atributos.find(item => item.atributo === MODULO_MODULO_TENSAO_VOC_ATTR)

            return {
              potenciaMaxima: Number(potenciaMaxima?.valor || 0),
              potenciaMinima: Number(potenciaMinima?.valor || 0),
              tensaoVocModulo: Number(tensaoVocModulo?.valor || 0),
              componente_id: modulo.id,
              inversoresTipos: [],
              categoria_componentes_id: modulo.categoria_componentes_id,
              potencia: Number(potencia?.valor || 0),
              faturamento: modulo.data_previsao_faturamento ? new Date(modulo.data_previsao_faturamento).toISOString() : undefined,
              nome: modulo.nome,
              descricao: modulo.descricao,
              quantidade: moduloSelecionado?.quantidade || 0,
              categoria: modulo.categoria_componentes_nome,
              altura: modulo.altura,
              largura: modulo.largura,
              peso: modulo.peso,
              valor: 0, // Calculado no backend
              comprimento: modulo.comprimento,
              valor_unitario: 0,
              regioes_parametros: modulo?.regioes_parametros,
              finame: modulo?.finame,
              exibir_popup_parceiro: modulo.exibir_popup_parceiro,
              selecionado: modulo.id === moduloSelecionado?.id
            }
          })

          Object.assign<typeof state["data"], Partial<typeof state["data"]>>(state.data, { modulosList: newModulos })
        })
      },
      addControladores: (controladores: VinculoModuloResponse[], controladoresSelecionados?: { id: number, quantidade: number }[]) => {
        update(({ state }) => {
          const newControladores: ListagemItem[] = controladores.map((item: VinculoModuloResponse): ListagemItem => {
            const maximaPotenciaFV = item.atributos.find(item => item.atributo === CONTROLADOR_MAXIMA_POTENCIA_FOTOVOLTAICA_ATTR);
            const savedItem = controladoresSelecionados?.find(i => i.id === item.id);

            return {
              potenciaMaximaEntradaCC: 0,
              tensaoMaximaEntrada: 0,
              maximaPotenciaFV: Number(maximaPotenciaFV?.valor || 0),
              componente_id: item.id,
              nome: item.nome,
              modelo: item?.modelo,
              descricao: item.descricao,
              quantidade: savedItem?.quantidade || 0,
              altura: item.altura,
              largura: item.largura,
              peso: item.peso,
              valor: 0,
              categoria_componentes_id: item.categoria_componentes_id,
              potencia: 0,
              valor_unitario: 0,
              exibir_popup_parceiro: item?.exibir_popup_parceiro,
            }
          })

          Object.assign<typeof state["data"], Partial<typeof state["data"]>>(state.data, { controladoresList: newControladores })
        })
      },
      addCarregadores: (carregadores: VinculoModuloResponse[], carregadoresSelecionados?: { id: number, quantidade: number }[]) => {
        update(({ state }) => {
          const newCarregadores: ListagemItem[] = carregadores.map((item: VinculoModuloResponse): ListagemItem => {
            const potencia = item.atributos.find(item => item.atributo === INVERTER_MAX_POWER_ATTR);
            const potenciaMaximaEntradaCC = item.atributos.find(item => item.atributo === INVERSOR_ONDA_POTENCIA_MAXIMA_ATTR);
            const tensaoMaximaEntrada = item.atributos.find(item => item.atributo === INVERSOR_CARREGADOR_TENSAO_MAXIMA_ENTRADA);
            const savedItem = carregadoresSelecionados?.find(i => i?.id === item.id);

            return {
              potenciaMaximaEntradaCC: Number(potenciaMaximaEntradaCC?.valor || 0),
              tensaoMaximaEntrada: Number(tensaoMaximaEntrada?.valor || 0),
              maximaPotenciaFV: 0,
              componente_id: item.id,
              nome: item.nome,
              modelo: item?.modelo,
              descricao: item.descricao,
              quantidade: savedItem?.quantidade || 0,
              altura: item.altura,
              largura: item.largura,
              peso: item.peso,
              valor: 0,
              categoria_componentes_id: item.categoria_componentes_id,
              potencia: Number(potencia?.valor || 0),
              valor_unitario: 0,
              exibir_popup_parceiro: item?.exibir_popup_parceiro,
            }
          })

          Object.assign<typeof state["data"], Partial<typeof state["data"]>>(state.data, { carregadoresList: newCarregadores })
        })
      },
      addEstruturas: (estruturas: EstruturaListagem[], componentesProjeto?: { id: number, quantidade: number, grupo_id: number }[]) => {
        update(({ state }) => {
          const newEstruturas: EstruturaListagem[] = estruturas.map(
            (estrutura: EstruturaListagem) => {
              const itens = estrutura.items.map((item) => {
                const componenteProjeto = componentesProjeto?.find(
                  (componente) =>
                    Number(componente.id) === Number(item.componente_id) &&
                    Number(componente.grupo_id) === Number(estrutura.estruturas_grupo_id)
                );

                return {
                  ...item,
                  quantidade: componenteProjeto?.quantidade ?? 0,
                };
              });

              const itemSelecionado = itens.find((item) => Number(item.quantidade) > 0);
              const quantidadeItem = itens?.reduce((acumulador, item) => acumulador + item.quantidade, 0) || 0;

              return {
                ...estrutura,
                componente_id: itemSelecionado?.componente_id ?? 0,
                items: itens,
                opened: quantidadeItem > 0,
                quantidade: quantidadeItem,
              };
            }
          );

          Object.assign<typeof state["data"], Partial<typeof state["data"]>>(state.data, { estruturas_edicao: newEstruturas })
        })
      },
      addAcessorios: (acessorios: ListagemItem[], componentesProjeto?: { id: number, quantidade: number }[]) => {
        update(({ state }) => {
          const newAcessorios: ListagemItem[] = acessorios.map((item) => {
            const componente = componentesProjeto?.find(i => i.id === item.componente_id)
            return {...item, quantidade: componente?.quantidade || 0}
          })

          Object.assign<typeof state["data"], Partial<typeof state["data"]>>(state.data, { acessorios: newAcessorios })
        })
      },
      calcularProjeto: async valorServico => {
        const { data } = get().state
        const modulo = data.modulosList?.filter(item => item.selecionado) || []
        const inversores = data.inversoresList?.filter(item => item.quantidade > 0) || []
        const estruturas = (data.estruturasSelecionadas || []).flatMap(group => group.items.filter(item => item.quantidade > 0))
        const acessorios = data.acessorios?.filter(item => item.quantidade > 0) || []
        const componentes: ComponenteCalcular[] = formatarComponentesCalcular([...modulo, ...inversores, ...estruturas, ...acessorios])
        const servico = data.recebe_servico && data.tipo_proposta?.value !== 'gerador-sem-instalacao'
          ? Number(valorServico ?? data.valor_servico ?? 0)
          : 0

        get().actions.changeLoading({ calcularProjeto: true })
        try {
          const payload: ProjetoCalcular = {
            componentes,
            estado_id: data.estado?.id || '',
            servicos: { tipo: servico ? 'revenda' as ProjetoServico : null, valor: servico },
            regioes: data.regioes.map(item => item.id),
            segmento: data.segmento,
            projeto_id: data.clonar_projeto ? undefined : (data.id || undefined),
            distribuidor_id: data.distribuidor?.id || undefined,
            revenda_id: data.revenda?.id || undefined,
            recalcular: projetosProvider.confereRecalcular(componentes, data),
          }
          const response = await projetosProvider.calcular(payload)
          get().actions.changeValue({
            resultado_calculos: response.data || null,
            valor_servico_pis_cofins: response.data?.servicos?.valor_com_pis_cofins || 0,
          })
        } finally {
          get().actions.changeLoading({ calcularProjeto: false })
        }
      },
      prepararSalvar: () => JSON.parse(JSON.stringify(get().state.data)),
      salvarProjeto: async save => {
        get().actions.changeLoading({ salvarProjeto: true })
        try { return await save(get().actions.prepararSalvar()) }
        finally { get().actions.changeLoading({ salvarProjeto: false }) }
      },
    },
  }
})
